import { apiFetch } from "@/lib/api";
import type { PublicEpisode, PublicParticipant } from "@/lib/explore";

export interface OnAirPerson {
  firstName: string;
  house: string | null;
  id: string;
}

export interface OnAirEpisode {
  cancelled: boolean;
  endOfEpisode: Date;
  hosts: OnAirPerson[];
  iconUrl: string | null;
  id: string;
  show: { description: string | null; id: string; name: string };
  special: boolean;
  startOfEpisode: Date;
  techies: OnAirPerson[];
  title: string;
  url: string | null;
}

const techRoles = new Set(["techie", "technician"]);

const peopleIn = (
  participants: PublicParticipant[],
  matches: (roleName: string) => boolean
): OnAirPerson[] =>
  participants
    .filter((participant) => matches(participant.roleName.toLowerCase()))
    .map((participant) => ({
      firstName: participant.firstName,
      house: participant.house,
      id: participant.contributorId,
    }));

export const lineUpOf = (participants: PublicParticipant[]) => ({
  hosts: peopleIn(participants, (role) => role === "host"),
  techies: peopleIn(participants, (role) => techRoles.has(role)),
});

const toOnAirEpisode = (episode: PublicEpisode): OnAirEpisode => ({
  cancelled: episode.cancelled,
  endOfEpisode: new Date(episode.endsAt),
  ...lineUpOf(episode.participants),
  iconUrl: episode.iconUrl,
  id: episode.id,
  show: {
    description: episode.showDescription,
    id: episode.showId,
    name: episode.showName,
  },
  special: episode.special,
  startOfEpisode: new Date(episode.startsAt),
  title: episode.title,
  url: episode.recordingUrl,
});

export async function getTodayEpisodes(): Promise<OnAirEpisode[]> {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);

  const query = new URLSearchParams({
    from: todayStart.toISOString(),
    limit: "100",
    to: todayEnd.toISOString(),
  });
  const data = await apiFetch<{ episodes: PublicEpisode[] }>(
    `/api/public/episodes?${query.toString()}`
  );

  return (data?.episodes ?? [])
    .map(toOnAirEpisode)
    .toSorted(
      (a, b) => a.startOfEpisode.getTime() - b.startOfEpisode.getTime()
    );
}

export function findCurrentlyAiringEpisode(episodes: OnAirEpisode[]) {
  const now = new Date();

  return (
    episodes.find(
      (episode) =>
        !episode.cancelled &&
        new Date(episode.startOfEpisode) <= now &&
        now <= new Date(episode.endOfEpisode)
    ) ?? null
  );
}

export function findNextAiringEpisode(episodes: OnAirEpisode[]) {
  const now = new Date();

  return (
    episodes.find((episode) => new Date(episode.startOfEpisode) > now) ?? null
  );
}

/** How far through the episode we are, 0–1. */
export function episodeProgress(episode: OnAirEpisode, now = new Date()) {
  const start = new Date(episode.startOfEpisode).getTime();
  const end = new Date(episode.endOfEpisode).getTime();

  if (end <= start) {
    return 0;
  }

  return Math.min(1, Math.max(0, (now.getTime() - start) / (end - start)));
}
